import React, {
    ReactElement,
    SetStateAction,
    useState,
    useEffect,
    useCallback,
    forwardRef,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
} from 'react';
import { Box } from '@mui/system';
import TypographyLevel from 'shared/components/Typography';

import { FlatPaginationComp } from 'shared/components/Pagination';

import { ClientService } from 'services';

import {
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Modal,
    Typography,
} from '@mui/material';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { Utils } from 'shared/helpers';
import { formattingAction } from 'shared/helpers/FormatAction';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import {
    DateRangePicker,
    DateRange,
} from 'shared/shim/materialui-daterange-picker';
import { Formik } from 'formik';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { downloadCSV, downloadPDF } from 'shared/helpers/DirectDownload';
import DateFnsAdapter from '@date-io/date-fns';
import { useNavigate } from 'react-router-dom';
import PendingIcon from '@mui/icons-material/Pending';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import { setChecklistRowsPerPageNumber } from 'state/persistapplication/Action';
import { useMountedState, useUpdateEffect } from 'react-use';
import { AxiosError } from 'axios';

const dateFns = new DateFnsAdapter();

type MCMain = {
    ref: React.Ref<inferMCMainBody>;
    setStatusPage: React.Dispatch<SetStateAction<string>>;
    setStatusClientID: React.Dispatch<SetStateAction<string>>;
    setStatusClientChecklist: React.Dispatch<SetStateAction<ClientCheckList[]>>;
    setStatusClient: React.Dispatch<SetStateAction<string>>;
    setStatusClientEmail: React.Dispatch<SetStateAction<string>>;
    setStatusClientPhone: React.Dispatch<SetStateAction<number>>;
    setStatusClientArchiveDays: React.Dispatch<SetStateAction<number>>;
    setStatusClientSLADays: React.Dispatch<SetStateAction<number>>;
    setStatusClientCM9Days: React.Dispatch<SetStateAction<number>>;
    setStatusClientReworkDays: React.Dispatch<SetStateAction<number>>;
    SetStatusClientChecklistStepData: React.Dispatch<
        SetStateAction<ChecklistStep[]>
    >;
    setSolicitorData: React.Dispatch<SetStateAction<any[]>>;
};

/**
 * MCMain component.
 * Renders the main content for managing clients.
 * @returns {ReactElement} MCMain component.
 */
const MCMain: React.FC<MCMain> = forwardRef(
    (
        {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            setStatusPage,
            setStatusClientID,
            setStatusClient,
            setStatusClientEmail,
            setStatusClientPhone,
            setStatusClientChecklist,
            setStatusClientArchiveDays,
            setStatusClientSLADays,
            setStatusClientCM9Days,
            setStatusClientReworkDays,
            SetStatusClientChecklistStepData,
            setSolicitorData,
        },
        ref
    ): ReactElement => {
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const isMounted = useMountedState();
        const [open, setOpen] = useState<boolean>(false);
        const [auditClientId, setAuditClientId] = useState<string>('');
        const [auditClientName, setAuditClientName] = useState<string>('');
        const [dateRange, setDateRange] = useState<DateRange>({});
        const [page, setPage] = useState(0);
        const [isLoading, setIsLoading] = useState<boolean>(true);
        const [isNetworkError, setNetworkError] = useState<boolean>(true);
        const [pageCount, setPageCount] = useState(0);
        const [clientData, setClientData] = useState<ClientListData[]>([]);
        const [search, setSearch] = useState<string>('');
        const [searchBy, setSearchBy] = useState<string>('');
        const [isDownloading, setDownloading] = useState<boolean>(false);
        const [isMount, setMounted] = useState<boolean>(false);
        const persistRowsPerPage = useSelector(
            (state: any) => state.persistState.rowsPerPage.checklist
        );
        const [rowsPerPage, setRowsPerPage] =
            useState<number>(persistRowsPerPage);
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const handleChangeRowsPerPage = useCallback(
            (
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
                setIsLoading(true);
            },
            [setIsLoading, setPage, setRowsPerPage]
        );

        useLayoutEffect(() => {
            dispatch(setChecklistRowsPerPageNumber(rowsPerPage));
        }, [rowsPerPage]);

        useEffect(() => {
            if (isMounted() && persistRowsPerPage !== rowsPerPage) {
                setRowsPerPage(persistRowsPerPage);
            }
        }, []);
        const innerPaginationComp = useRef<HTMLDivElement>(null);
        useImperativeHandle(
            ref,
            () => ({
                setSearch: (val) => {
                    setSearch(val);
                },
                setSearchBy: (val) => {
                    setSearchBy(val);
                },
            }),
            []
        );

        const refreshGetClientListBasedOnPagination = useCallback(
            (currentPage: number, _rowsPerPage: number): void => {
                ClientService.getClientList({
                    search,
                    searchBy,
                    offset: currentPage,
                    limit: _rowsPerPage,
                })
                    .then(
                        (response: {
                            data: {
                                msg: string;
                                data: {
                                    paginatedResult: ClientListData[];
                                    totalCount: { count: number }[];
                                }[];
                            };
                        }) => {
                            if (
                                response.data.data.length > 0 &&
                                response.data.data.some(
                                    (o) =>
                                        o.totalCount.some(
                                            (i) => i.count > 0
                                        ) === true
                                )
                            ) {
                                const { paginatedResult, totalCount } =
                                    response.data.data[0];
                                const pageCount = totalCount[0].count;
                                setPageCount(pageCount);

                                setClientData(
                                    Array.from(paginatedResult).map(
                                        (element) => ({
                                            ...element,
                                            actions: formattingAction(element),
                                        })
                                    )
                                );
                                setIsLoading(false);
                            } else {
                                setPageCount(0);
                                setClientData([]);
                                setIsLoading(false);
                            }
                        }
                    )
                    .catch((error) => {
                        if (
                            error !== null &&
                            error instanceof AxiosError &&
                            error.code === 'ERR_NETWORK'
                        ) {
                            setNetworkError(true);
                        } else {
                            Utils.handleErrorResponse(error);
                        }
                    });
            },
            [search, searchBy]
        );
        useLayoutEffect(() => {
            setNetworkError(false);
        }, [isLoading]);
        useEffect(() => {
            setMounted(true);
            refreshGetClientListBasedOnPagination(page + 1, rowsPerPage);
        }, []);
        useUpdateEffect(() => {
            if (isMount === true) {
                setIsLoading(true);
                refreshGetClientListBasedOnPagination(page + 1, rowsPerPage);
            }
        }, [page, search, searchBy, rowsPerPage]);
        /**
         * Handles pagination page change.
         * @param {object} event - The event object.
         * @param {number} newPage - The new page number.
         * @returns {void}
         */
        const onChangePagination = (event: any, newPage: number): void => {
            setPage(newPage - 1);
        };
        const [isEditable, setEditable] = React.useState<boolean>(false);
        const [isEdited, setEdited] = React.useState<number | null>(null);
        const handleEditClientAction = useCallback(
            async (clientId: string) => {
                setEditable(true);
                await ClientService.getClient({
                    clientId,
                })
                    .then(
                        (response: {
                            data: {
                                msg: string;
                                data: ClientData;
                            };
                        }) => {
                            setEdited(null);
                            // setEditable(false);
                            setStatusClient(response.data.data.name);
                            setStatusClientEmail(response.data.data.email);
                            setStatusClientPhone(
                                response.data.data.contactNumber
                            );
                            setStatusClientArchiveDays(
                                response.data.data.removePacketInDays
                            );
                            setStatusClientSLADays(
                                response.data.data.timeToSla
                            );
                            setStatusClientCM9Days(
                                response.data.data.timeToCm9
                            );
                            setStatusClientReworkDays(
                                response.data.data.timeToRework
                            );
                            const solicitorDataState = Array.from(
                                response.data.data.solicitors
                            ).map((el) => ({
                                ...el,
                                contacts:
                                    Array.isArray(el.contacts) &&
                                    el.contacts.length > 0
                                        ? Array.from(el.contacts)
                                              .map((contact) => ({
                                                  name: contact.name,
                                                  email:
                                                      typeof contact.email !==
                                                      'string'
                                                          ? contact.email.id
                                                          : contact.email,
                                                  phone: contact.phone,
                                                  isAdded: true,
                                                  isAddition: false,
                                              }))
                                              .concat([
                                                  {
                                                      name: '',
                                                      email: '',
                                                      phone: '',
                                                      isAdded: false,
                                                      isAddition: false,
                                                  },
                                              ])
                                        : [
                                              {
                                                  name: '',
                                                  email: '',
                                                  phone: '',
                                                  isAdded: false,
                                                  isAddition: false,
                                              },
                                          ],
                            }));
                            setSolicitorData(solicitorDataState);
                            setStatusClientChecklist(
                                response.data.data.checkList
                            );
                            const checklistStepData = Array.from(
                                response.data.data.checkListSteps
                            )
                                .sort((a, b) => a.step - b.step)
                                .map((el) => ({
                                    ...el,
                                    actions: formattingAction(el),
                                }));
                            SetStatusClientChecklistStepData(checklistStepData);
                            const dataUUID = formattingAction(
                                response.data.data
                            );
                            setStatusClientID(dataUUID.uuid);

                            navigate(
                                `/manage-checklists/edit/${dataUUID.uuid}`,
                                {
                                    state: {
                                        clientPhone:
                                            response.data.data.contactNumber,
                                        clientName: response.data.data.name,
                                        clientEmail: response.data.data.email,
                                        slaDays: response.data.data.timeToSla,
                                        cm9Days: response.data.data.timeToCm9,
                                        reworkDays:
                                            response.data.data.timeToRework,
                                        archiveDays:
                                            response.data.data
                                                .removePacketInDays,
                                        clientSolicitorData: solicitorDataState,
                                        clientChecklist:
                                            response.data.data.checkList,
                                        clientChecklistStep: checklistStepData,
                                    },
                                }
                            );
                        }
                    )
                    .catch((error) => {
                        setEdited(null);
                        setEditable(false);
                        Utils.handleErrorResponse(error);
                    });
            },
            [
                SetStatusClientChecklistStepData,
                navigate,
                setSolicitorData,
                setStatusClient,
                setStatusClientArchiveDays,
                setStatusClientCM9Days,
                setStatusClientChecklist,
                setStatusClientEmail,
                setStatusClientID,
                setStatusClientPhone,
                setStatusClientReworkDays,
                setStatusClientSLADays,
            ]
        );
        /**
         * Downloads an array of files based on the provided URLs.
         *
         * @param arrayOfObjects - "file" property with the URL.
         * @returns {Promise<void>} A promise that resolves when all files are downloaded.
         */
        // const downloadFileOnSaS = async (fileOnSaS: string): Promise<void> => {
        //     try {
        //         const fileName: any = fileOnSaS.split('/').pop();
        //         await downloadFile(fileOnSaS, fileName);

        //         setDownloading(false);
        //     } catch (error) {
        //         setDownloading(false);
        //         Toast.showErrorMessage('Error when download file');
        //     }
        // };

        const handleToggleDateRangePicker = useCallback(() => {}, []);
        const handleModalDateRangePicker = useCallback(() => {
            if (!isDownloading) setOpen(false);
        }, [isDownloading]);
        const handleOpenAuditFileModal = useCallback(
            (clientId: string, clientName: string) => {
                setOpen(true);
                setAuditClientId(clientId);
                setAuditClientName(clientName);
            },
            []
        );
        /**
         * get all pending PDF using fetch download
         *
         * @param pendingDocs - "string" array of url document to download.
         * @returns {Promise<Blob>} A promise that resolves when all zip files are downloaded.
         */
        const getPendingPDF = useCallback<
            (pendingDocs: string[]) => Promise<Blob>[]
        >(
            (pendingZips: string[]) =>
                Array.from(pendingZips).map(
                    (el) =>
                        new Promise(async (resolve, reject) => {
                            downloadPDF(el).then(resolve).catch(reject);
                        })
                ),
            []
        );
        /**
         * get all pending PDF using fetch download
         *
         * @param pendingDocs - "string" array of url document to download.
         * @returns {Promise<Blob>} A promise that resolves when all zip files are downloaded.
         */
        const getPendingCSV = useCallback<
            (pendingDocs: string[]) => Promise<Blob>[]
        >(
            (pendingZips: string[]) =>
                Array.from(pendingZips).map(
                    (el) =>
                        new Promise(async (resolve, reject) => {
                            downloadCSV(el).then(resolve).catch(reject);
                        })
                ),
            []
        );
        /**
         * Downloads an Zips based on the audited file url.
         *
         * @param arrayOfPendingPDF - "file" url in array.
         * @param arrayOfPendingCSV - "file" url in array.
         * @param packetId - "packetId" in string
         * @returns {Promise<void>} A promise that resolves when all files are downloaded.
         */
        const downloadAuditZips = useCallback(
            async (
                arrayOfPendingPDF: string,
                arrayOfPendingCSV: string,
                clientName: string,
                dateofAudit: string
            ): Promise<void> =>
                new Promise(async (resolve, reject) => {
                    let promisePendingBlobs: Blob[] = [];

                    try {
                        if (arrayOfPendingPDF) {
                            const promisePendingDocs = getPendingPDF([
                                arrayOfPendingPDF,
                            ]);
                            const pendingBlobFromDocs = await Promise.all<Blob>(
                                promisePendingDocs
                            );
                            promisePendingBlobs =
                                promisePendingBlobs.concat(pendingBlobFromDocs);
                        }
                        if (arrayOfPendingCSV) {
                            const promisePendingCSV = await getPendingCSV([
                                arrayOfPendingCSV,
                            ]);
                            const pendingBlobFromCSV = await Promise.all<Blob>(
                                promisePendingCSV
                            );
                            promisePendingBlobs =
                                promisePendingBlobs.concat(pendingBlobFromCSV);
                        }

                        // create new zip when there is processedFile
                        const zip = new JSZip();

                        promisePendingBlobs.forEach((blob) => {
                            if (blob.type === 'application/pdf') {
                                zip.file(
                                    `${dateofAudit}_ChecklistsAudit_${clientName}.pdf`,
                                    blob
                                );
                            } else {
                                zip.file(
                                    `${dateofAudit}_ChecklistsAudit_${clientName}.csv`,
                                    blob
                                );
                            }
                        });

                        zip.generateAsync({ type: 'blob' }).then((content) => {
                            // see FileSaver.js
                            FileSaver.saveAs(
                                content,
                                `Audit Cross check records for ${clientName} at ${dateofAudit}`
                            );
                            resolve();
                        });
                        resolve();
                    } catch (error) {
                        reject(error);
                        Utils.handleErrorResponse(error);
                    }
                }),
            [getPendingCSV, getPendingPDF]
        );
        const handleDownloadDocumentAudit = useCallback(() => {
            if (dateRange && dateRange.startDate && dateRange.endDate) {
                setDownloading(true);
                const fromDate = dateFns.formatByString(
                    dateRange.startDate,
                    'dd-MM-yyyy'
                );
                const toDate = dateFns.formatByString(
                    dateRange.endDate,
                    'dd-MM-yyyy'
                );
                ClientService.getClientAudit({
                    clientId: auditClientId,
                    fromDate,
                    toDate,
                })
                    .then(
                        (response: {
                            data: {
                                data: {
                                    csvAuditFile: string;
                                    pdfAuditFile: string;
                                };
                            };
                        }) => {
                            downloadAuditZips(
                                response.data.data.pdfAuditFile,
                                response.data.data.csvAuditFile,
                                auditClientName,
                                `${fromDate} to ${toDate}`
                            )
                                .then(() => {
                                    setDownloading(false);
                                    setOpen(false);
                                })
                                .catch(() => {
                                    setDownloading(false);
                                    setOpen(false);
                                });
                        }
                    )
                    .catch((response) => {
                        setDownloading(false);
                        Utils.handleErrorResponse(response);
                    });
            }
        }, [auditClientId, auditClientName, dateRange, downloadAuditZips]);

        const isLoadingProgress = React.useMemo(
            () => isEditable || isLoading,
            [isLoading, isEditable]
        );

        return (
            <Box>
                <Box className="WrapperManageClient">
                    {isLoadingProgress && isNetworkError === false && (
                        <Box className="align-loader">
                            <CircularProgress
                                title="progress loading"
                                className="mr-2 margin-loader"
                                size={54}
                                color="inherit"
                            />
                        </Box>
                    )}
                    {isLoadingProgress && isNetworkError && (
                        <EmptyPlaceholder message="No internet connection, Please try again!" />
                    )}
                    {!isLoadingProgress && clientData.length < 1 && (
                        <EmptyPlaceholder message="There are no Checklists records yet" />
                    )}
                    {!isLoadingProgress &&
                        clientData &&
                        clientData.length > 0 &&
                        clientData.map((data: ClientListData, index) => (
                            <Box key={index} className="list-client">
                                <Grid
                                    container
                                    spacing={2}
                                    className="container-client"
                                >
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Box className="content-client">
                                            <TypographyLevel variant="fifth">
                                                Checklists Name
                                            </TypographyLevel>
                                            <TypographyLevel
                                                variant="three"
                                                className="desc-client"
                                            >
                                                {data.name}
                                            </TypographyLevel>
                                        </Box>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={2}
                                        className="word-break"
                                    >
                                        <Box className="content-client">
                                            <TypographyLevel variant="fifth">
                                                Inquire Email
                                            </TypographyLevel>
                                            <TypographyLevel
                                                variant="three"
                                                className="desc-client"
                                            >
                                                {data.email}
                                            </TypographyLevel>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Box className="content-client">
                                            <TypographyLevel variant="fifth">
                                                Company phone
                                            </TypographyLevel>
                                            <TypographyLevel
                                                variant="three"
                                                className="desc-client word-break"
                                            >
                                                {data.contactNumber}
                                            </TypographyLevel>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Box className="content-client">
                                            <TypographyLevel variant="fifth">
                                                Checklist approval
                                            </TypographyLevel>
                                            <TypographyLevel
                                                variant="three"
                                                className="desc-client"
                                            >
                                                {data.enabled
                                                    ? 'Approved'
                                                    : 'Pending'}
                                            </TypographyLevel>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={2}>
                                        {data.enabled && (
                                            <Button
                                                className="BoxActionClient"
                                                onClick={() => {
                                                    handleOpenAuditFileModal(
                                                        data.clientId,
                                                        data.name
                                                    );
                                                }}
                                                title="toggle history"
                                            >
                                                <HistoryToggleOffIcon
                                                    width={20}
                                                    height={20}
                                                />
                                            </Button>
                                        )}
                                        <IconButton
                                            className="BoxActionClient"
                                            disabled={isEditable}
                                            onClick={() => {
                                                setEdited(index);
                                                handleEditClientAction(
                                                    data.clientId
                                                );
                                            }}
                                            title="edit checklist"
                                        >
                                            {isEditable &&
                                            isEdited === index ? (
                                                <PendingIcon />
                                            ) : (
                                                <img
                                                    src="/images/icons/EditPencil.svg"
                                                    width={20}
                                                    height={20}
                                                    alt="Edit Checklist"
                                                />
                                            )}
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}

                    {Array.isArray(clientData) &&
                        clientData.length > 0 &&
                        isEditable === false && (
                            <Box className="pagination">
                                <FlatPaginationComp
                                    hidden={
                                        (!isLoading &&
                                            clientData.length >= 1) === false
                                    }
                                    className="pagination-manage-client"
                                    innerRef={innerPaginationComp}
                                    data={pageCount}
                                    page={page}
                                    count={Math.ceil(pageCount / rowsPerPage)}
                                    onPageChange={onChangePagination}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={
                                        handleChangeRowsPerPage
                                    }
                                    onChange={onChangePagination}
                                    popupPrevNext
                                    containerClassName="manageClient"
                                />
                            </Box>
                        )}
                </Box>
                <Modal
                    open={open}
                    disableEscapeKeyDown={isDownloading}
                    aria-labelledby="parent-modal-title"
                    aria-describedby="parent-modal-description"
                    onClose={handleModalDateRangePicker}
                >
                    <Box className="container-audit-modal">
                        <Typography variant="h3" id="parent-modal-title">
                            Download Audit
                        </Typography>
                        <Box className="container-completed-detail">
                            <Typography>Fill the date ranges</Typography>
                            <Typography>
                                Choose to "Proceed" to download audit file.
                            </Typography>
                        </Box>
                        <DateRangePicker
                            wrapperClassName="date-rangepicker-audit"
                            open
                            toggle={handleToggleDateRangePicker}
                            onChange={(range) => setDateRange(range)}
                        />
                        <Box className="escalated-solicitor-action-history-audit">
                            <Formik
                                initialValues={{
                                    smallInput: 'Insert Here',
                                }}
                                enableReinitialize
                                onSubmit={() => {
                                    // console.log('');
                                }}
                            >
                                <Box className="containerButton">
                                    <AppPrimaryButton
                                        title="Cancel"
                                        onClick={() => setOpen(false)}
                                        className="btn-small back-button"
                                    />
                                    <AppPrimaryButton
                                        title="Proceed"
                                        className="btn-small"
                                        isLoading={isDownloading}
                                        loadingSize={22}
                                        disabled={
                                            (dateRange &&
                                                Object.keys(dateRange).includes(
                                                    'startDate'
                                                ) === false &&
                                                Object.keys(dateRange).includes(
                                                    'endDate'
                                                ) === false) ||
                                            isDownloading
                                        }
                                        onClick={handleDownloadDocumentAudit}
                                    />
                                </Box>
                            </Formik>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        );
    }
);

export default MCMain;
